import { routerPaths } from "./routerPaths";

/**
 * Permission-gated landing pages, in order; the resolver returns the first one
 * the role can access.
 *
 * Home leads the list — every subgroup holds `homepage_page_access`
 * (permissions/mapping.py), so it always matches once the Homepage v2 board is
 * enabled for the company. Until then it is skipped entirely and the legacy
 * file lists below it are what a user lands on, which is what lets the board
 * ship to production switched off.
 */
const GATED_LANDING_CANDIDATES: ReadonlyArray<{ path: string; permission: string }> = [
    { path: routerPaths.home, permission: "homepage_page_access" },
    { path: routerPaths.files.list, permission: "files_all_page_access" },
    { path: routerPaths.files.tbgl, permission: "tbgl_files_page_access" },
    { path: routerPaths.files.finJournals, permission: "finjournal_files_view" },
];

/**
 * The welcome page is the fallback. Every file page belongs to a customer
 * product module, so a company whose customer module has none (or that has no
 * customer module yet) holds none of the permissions above. The welcome page
 * needs no permission and calls no customer-module API.
 */
const FALLBACK_LANDING_PATH = routerPaths.welcome;

export type TResolveLandingPathOptions = {
    /** The company's `homepage_v2_enabled` flag. Defaults to false so a caller
     * that has not loaded the flags yet can never land someone on a board
     * their company cannot open. */
    homepageEnabled?: boolean;
    /** Base path of the company's customer module pages (`""` for Rev EU). */
    customerModuleBasePath?: string;
};

/**
 * Resolve the first page the given permission set can access. Used to redirect a
 * (possibly emulated) role away from a page it cannot view, instead of showing a
 * "no access" message. Always returns a path — the welcome page is the fallback.
 */
export const resolveLandingPath = (
    permissions: string[],
    { homepageEnabled = false, customerModuleBasePath = "" }: TResolveLandingPathOptions = {},
): string => {
    const candidates = homepageEnabled
        ? GATED_LANDING_CANDIDATES
        : GATED_LANDING_CANDIDATES.filter((candidate) => candidate.path !== routerPaths.home);
    const match = candidates.find((candidate) => permissions.includes(candidate.permission));
    // Candidates are customer module pages, listed as Rev EU addresses them.
    return match ? `${customerModuleBasePath}${match.path}` : FALLBACK_LANDING_PATH;
};
